import { useRouter } from "@/_router";
import { useUserSlice } from "@/models/useUserSlice";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Settings from "./settings";
import {
  SettingsIcon,
  CrownIcon,
  LibraryIcon,
  CloseIcon,
  HeartIcon,
  HeartFilledIcon,
  HomeIcon,
  ShareIcon,
} from "@/components/Icons";
import { useUISlice } from "@/models/useUISlice";
import Skeleton from "@/components/Skeleton";
import WebContainer from "@/components/WebContainer";
import { getPlatform } from "@/models/platform";
import { version } from "../../../package.json";
import { setMetaThemeColor } from "@/models/useTheme";
import { useAuth } from "@/models/useAuth";
import { i18n } from "@/locales";
import { logFunnelEvent } from "@/fetchData/datadog";
import type { Book, ContentLanguage } from "@/models/types";
import { CONTENT_LANGUAGES } from "@/models/types";
import { useDismissSheet } from "@/models/useDismissSheet";
import { shareBook } from "@/models/useNative";

/** Scroll tutorial overlay for first-time users */
const ScrollTutorial = ({ onDismiss }: { onDismiss: () => void }) => {
  const { t } = useTranslation();

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="relative flex flex-col items-center gap-4 animate-fade-in-up">
        <p className="text-white text-lg font-bold px-6 text-center">
          {t("home.scrollTutorial")}
        </p>
        <div className="animate-scroll-hint">
          <svg
            width="36"
            height="36"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
        <button
          onClick={onDismiss}
          className="mt-4 px-8 py-3 rounded-full bg-white/20 backdrop-blur-md text-white font-bold text-base active:scale-95 transition-transform border border-white/20"
        >
          {t("home.gotIt")}
        </button>
      </div>

      <style>{`
        @keyframes fade-in-up {
          0% { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes scroll-hint {
          0%, 100% { opacity: 0.4; transform: translateY(0); }
          50% { opacity: 1; transform: translateY(12px); }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.6s ease-out both;
        }
        .animate-scroll-hint {
          animation: scroll-hint 1.2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

/** Book detail bottom sheet — full description + read now */
const BookDetailSheet = ({
  book,
  onClose,
  onRead,
}: {
  book: Book;
  onClose: () => void;
  onRead: () => void;
}) => {
  const { t } = useTranslation();
  const likedBooks = useUserSlice((state) => state.likedBooks);
  const isLiked = likedBooks.includes(book.bookId);

  const toggleLiked = () => {
    const pref = useUserSlice.getState().preference();
    if (isLiked) {
      pref.removeFromLikedBooks(book.bookId);
    } else {
      pref.addToLikedBooks(book.bookId);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-md" />
      <div
        className="relative w-full max-w-md max-h-[80dvh] bg-white/10 backdrop-blur-2xl rounded-t-[40px] overflow-y-auto border-t border-white/20"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pb-[calc(env(safe-area-inset-bottom,24px)+24px)] pt-6">
          {book.aiDescription && (
            <p className="text-white/90 text-base leading-relaxed mb-4 font-medium">
              {book.aiDescription}
            </p>
          )}

          <div className="flex gap-3 mt-6">
            <button
              onClick={() => {
                onClose();
                onRead();
              }}
              className="flex-1 h-16 rounded-[32px] bg-white/20 backdrop-blur-md text-white font-bold text-lg active:scale-[0.97] transition-transform border border-white/20 flex items-center justify-center"
            >
              {t("home.readNow")}
            </button>
            <button
              onClick={toggleLiked}
              className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-md border border-white/20 flex items-center justify-center active:scale-90 transition-transform"
            >
              {isLiked ? (
                <HeartFilledIcon size={26} className="text-red-500" />
              ) : (
                <HeartIcon size={26} className="text-white" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/** Language picker bottom sheet */
const LanguagePicker = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation();
  const currentLang = useUserSlice((state) => state.contentLanguage);

  const handleSelect = (code: ContentLanguage) => {
    useUserSlice.getState().setContentLanguage(code);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-[var(--bg-primary)]/50 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-md max-h-[70dvh] bg-[var(--glass-bg)] backdrop-blur-3xl rounded-t-[40px] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-[var(--glass-bg)]/80 backdrop-blur-md pt-6 pb-2 px-6 z-10">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-[var(--text-primary)] font-bold text-2xl tracking-tight">
              {t("home.contentLanguageTitle")}
            </h2>
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-[var(--glass-bg-hover)] active:bg-[var(--glass-bg-active)] transition-colors"
            >
              <CloseIcon size={22} className="text-[var(--text-secondary)]" />
            </button>
          </div>
        </div>
        <div className="px-6 pb-[calc(env(safe-area-inset-bottom,24px)+24px)] pt-2">
          <div className="flex flex-col gap-1">
            {CONTENT_LANGUAGES.map(({ code, flag }) => (
              <button
                key={code}
                onClick={() => handleSelect(code)}
                className="flex items-center justify-between px-4 py-4 rounded-2xl active:bg-white/10 transition-colors"
              >
                <span className="text-[var(--text-primary)] text-base font-medium">
                  {flag} {t(`home.contentLanguage.${code}`)}
                </span>
                {currentLang === code && (
                  <span className="text-[var(--accent)] text-lg font-bold">✓</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/** Individual feed card with blur-up image loading */
const FeedCard = ({
  book,
  onRead,
  onReadBook,
  onSettingsOpenChange,
  hideActions,
  isDesktop,
}: {
  book: Book;
  onRead: () => void;
  onReadBook: (bookId: string) => void;
  onSettingsOpenChange?: (open: boolean) => void;
  hideActions?: boolean;
  isDesktop?: boolean;
}) => {
  const { t } = useTranslation();
  const [imgLoaded, setImgLoaded] = useState(false);
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const isSubscribed = useUserSlice((state) => state.isSubscribed);
  const contentLanguage = useUserSlice((state) => state.contentLanguage);
  const contentFlag = useMemo(
    () => CONTENT_LANGUAGES.find((l) => l.code === contentLanguage)?.flag ?? contentLanguage,
    [contentLanguage]
  );

  const likedBooks = useUserSlice((state) => state.likedBooks);
  const isLiked = likedBooks.includes(book.bookId);

  const toggleLiked = () => {
    const pref = useUserSlice.getState().preference();
    if (isLiked) {
      pref.removeFromLikedBooks(book.bookId);
    } else {
      pref.addToLikedBooks(book.bookId);
    }
  };

  useDismissSheet(isDetailOpen, useCallback(() => setIsDetailOpen(false), []));
  useDismissSheet(isLibraryOpen, useCallback(() => setIsLibraryOpen(false), []));
  useDismissSheet(isLangOpen, useCallback(() => setIsLangOpen(false), []));

  return (
    <div 
      className={isDesktop ? 'h-[100dvh] w-full snap-center snap-always flex justify-center items-center py-4' : 'h-[100dvh] snap-start snap-always relative'} 
      data-book-id={book.bookId}
    >
      {isDesktop ? (
        <div className="relative h-full aspect-[9/16] max-w-[calc(100vw-140px)] flex-shrink-0">
          <button onClick={onRead} className="absolute inset-0 w-full h-full rounded-2xl overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.4)] cursor-pointer group">
            {book.aiCoverBlurDataUri && (
              <div
                className="absolute inset-0 w-full h-full"
                style={{
                  backgroundImage: `url(${book.aiCoverBlurDataUri})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              />
            )}
            {book.aiCoverUrl ? (
              <img
                src={book.aiCoverUrl}
                alt=""
                className={`absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 ${imgLoaded ? "opacity-100" : "opacity-0"}`}
                loading="lazy"
                decoding="async"
                onLoad={() => setImgLoaded(true)}
              />
            ) : (
              <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-neutral-800 to-neutral-950" />
            )}
          </button>

          {/* Desktop Right Actions */}
          <div className="absolute left-[calc(100%+24px)] bottom-8 flex flex-col items-center gap-5 z-10">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsLibraryOpen(true);
              }}
              className="flex flex-col items-center gap-1 active:scale-90 transition-transform"
            >
              <div className="w-14 h-14 rounded-full bg-white/10 backdrop-blur-2xl flex items-center justify-center hover:bg-white/20 transition-all">
                <LibraryIcon size={26} className="text-white drop-shadow-md" />
              </div>
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsLangOpen(true);
              }}
              className="flex flex-col items-center gap-1 active:scale-90 transition-transform"
            >
              <div className="w-14 h-14 rounded-full bg-white/10 backdrop-blur-2xl flex items-center justify-center hover:bg-white/20 transition-all">
                <span className="text-2xl drop-shadow-md">{contentFlag}</span>
              </div>
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                shareBook(book);
              }}
              className="flex flex-col items-center gap-1 active:scale-90 transition-transform"
            >
              <div className="w-14 h-14 rounded-full bg-white/10 backdrop-blur-2xl flex items-center justify-center hover:bg-white/20 transition-all">
                <ShareIcon size={26} className="text-white drop-shadow-md" />
              </div>
            </button>

            <SettingsButton onOpenChange={onSettingsOpenChange} isDesktop />

            {!isSubscribed && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  useUISlice.getState().setPaywallVisible({ utmSource: "home_crown_button" });
                }}
                className="flex flex-col items-center gap-1 active:scale-90 transition-transform animate-bounce mt-2"
              >
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 shadow-[0_0_16px_rgba(251,191,36,0.5)] flex items-center justify-center">
                  <CrownIcon size={28} className="text-white drop-shadow-md" />
                </div>
              </button>
            )}
          </div>

          {/* Desktop Bottom Info Overlay */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent pt-32 pb-8 px-8 pointer-events-none rounded-b-2xl">
            <div className="max-w-[90%] pointer-events-auto">
              {book.aiDescription && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsDetailOpen(true);
                  }}
                  className="text-left active:opacity-70 transition-opacity"
                >
                  <p className="text-white/95 text-base font-medium leading-relaxed line-clamp-3 drop-shadow-md">
                    {book.aiDescription}
                  </p>
                  <p className="text-white/70 text-sm font-bold mt-2 drop-shadow-md">
                    {t("home.moreInfo")}
                  </p>
                </button>
              )}
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Full-screen cover image with blur-up */}
          <button onClick={onRead} className="absolute inset-0 w-full h-full cursor-pointer">
            {book.aiCoverBlurDataUri && (
              <div
                className="absolute inset-0 w-full h-full"
                style={{
                  backgroundImage: `url(${book.aiCoverBlurDataUri})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              />
            )}
            {book.aiCoverUrl ? (
              <img
                src={book.aiCoverUrl}
                alt=""
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${imgLoaded ? "opacity-100" : "opacity-0"}`}
                loading="lazy"
                decoding="async"
                onLoad={() => setImgLoaded(true)}
              />
            ) : (
              <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-neutral-800 to-neutral-950" />
            )}
          </button>

          {/* Right-side action column (mobile only) */}
          {!hideActions && (
            <div className="absolute right-4 bottom-[25%] flex flex-col items-center gap-6 z-10">
              {!isSubscribed && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    useUISlice.getState().setPaywallVisible({ utmSource: "home_crown_button" });
                  }}
                  className="flex flex-col items-center gap-1 active:scale-90 transition-transform animate-bounce"
                >
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 shadow-[0_0_16px_rgba(251,191,36,0.5)] flex items-center justify-center">
                    <CrownIcon size={28} className="text-white drop-shadow-md" />
                  </div>
                </button>
              )}

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsLangOpen(true);
                }}
                className="flex flex-col items-center gap-1 active:scale-90 transition-transform"
              >
                <div className="w-14 h-14 rounded-full bg-[var(--glass-bg)] backdrop-blur-2xl shadow-lg flex items-center justify-center">
                  <span className="text-2xl drop-shadow-md">
                    {contentFlag}
                  </span>
                </div>
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsLibraryOpen(true);
                }}
                className="flex flex-col items-center gap-1 active:scale-90 transition-transform"
              >
                <div className="w-14 h-14 rounded-full bg-[var(--glass-bg)] backdrop-blur-2xl shadow-lg flex items-center justify-center">
                  <LibraryIcon
                    size={28}
                    className="text-[var(--text-primary)] drop-shadow-md"
                  />
                </div>
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  shareBook(book);
                }}
                className="flex flex-col items-center gap-1 active:scale-90 transition-transform"
              >
                <div className="w-14 h-14 rounded-full bg-[var(--glass-bg)] backdrop-blur-2xl shadow-lg flex items-center justify-center">
                  <ShareIcon
                    size={28}
                    className="text-[var(--text-primary)] drop-shadow-md"
                  />
                </div>
              </button>

              <SettingsButton onOpenChange={onSettingsOpenChange} />
            </div>
          )}

          {/* Bottom gradient overlay with tappable description */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent pt-24 pb-[calc(env(safe-area-inset-bottom,24px)+24px)] px-6 pointer-events-none">
            <div className="max-w-[480px] mx-auto pointer-events-auto pr-20">
              {book.aiDescription && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsDetailOpen(true);
                  }}
                  className="text-left active:opacity-70 transition-opacity"
                >
                  <p className="text-white/95 text-base font-medium leading-relaxed line-clamp-3 drop-shadow-md">
                    {book.aiDescription}
                  </p>
                  <p className="text-white/70 text-sm font-bold mt-2 drop-shadow-md">
                    {t("home.moreInfo")}
                  </p>
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {isDetailOpen && (
        <BookDetailSheet
          book={book}
          onClose={() => setIsDetailOpen(false)}
          onRead={onRead}
        />
      )}

      {isLibraryOpen && (
        <LibrarySheet
          onClose={() => setIsLibraryOpen(false)}
          onRead={onReadBook}
        />
      )}

      {isLangOpen && (
        <LanguagePicker onClose={() => setIsLangOpen(false)} />
      )}
    </div>
  );
};

/** Settings button that opens settings sheet */
const SettingsButton = ({ onOpenChange, isDesktop }: { onOpenChange?: (open: boolean) => void, isDesktop?: boolean }) => {
  const [open, setOpen] = useState(false);

  const updateOpen = useCallback((value: boolean) => {
    setOpen(value);
    onOpenChange?.(value);
  }, [onOpenChange]);

  useDismissSheet(open, useCallback(() => updateOpen(false), [updateOpen]));

  return (
    <>
      <button
        onClick={(e) => {
          e.stopPropagation();
          updateOpen(true);
        }}
        className="flex flex-col items-center gap-1 active:scale-90 transition-transform"
      >
        <div className={`rounded-full flex items-center justify-center transition-all ${isDesktop ? 'w-14 h-14 bg-white/10 backdrop-blur-2xl hover:bg-white/20' : 'w-14 h-14 bg-[var(--glass-bg)] backdrop-blur-2xl shadow-lg'}`}>
          <SettingsIcon
            size={isDesktop ? 26 : 28}
            className={isDesktop ? "text-white drop-shadow-md" : "text-[var(--text-primary)] drop-shadow-md"}
          />
        </div>
      </button>
      {open && (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-[var(--bg-primary)]/40 backdrop-blur-sm"
            onClick={() => updateOpen(false)}
          />
          <div className="absolute top-0 right-0 h-full w-full max-w-md bg-[var(--bg-primary)] shadow-2xl animate-slide-in-right">
            <Settings onClose={() => updateOpen(false)} />
          </div>
        </div>
      )}
    </>
  );
};

/** Library sheet overlay */
const LibrarySheet = ({ onClose, onRead }: { onClose: () => void; onRead: (bookId: string) => void }) => {
  const { t } = useTranslation();
  const likedBooks = useUserSlice((state) => state.likedBooks);
  const readingProgress = useUserSlice((state) => state.readingProgress);
  const homeBooks = useUserSlice((state) => state.homeBooks);
  const [tab, setTab] = useState<"reading" | "liked">("reading");

  const bookMap = useMemo(() => {
    const map = new Map<string, Book>();
    homeBooks.forEach((b) => map.set(b.bookId, b));
    return map;
  }, [homeBooks]);

  const inProgressBooks = useMemo(() => {
    return Object.entries(readingProgress)
      .sort(
        ([, a], [, b]) =>
          new Date(b.lastReadAt).getTime() - new Date(a.lastReadAt).getTime()
      )
      .map(([bookId, prog]) => ({ book: bookMap.get(bookId), progress: prog }))
      .filter((x) => x.book);
  }, [readingProgress, bookMap]);

  const likedBooksList = useMemo(() => {
    return likedBooks
      .map((id) => bookMap.get(id))
      .filter(Boolean) as Book[];
  }, [likedBooks, bookMap]);

  const handleBookRead = (bookId: string) => {
    onClose();
    onRead(bookId);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-[var(--bg-primary)]/50 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-md max-h-[85dvh] bg-[var(--glass-bg)] backdrop-blur-3xl rounded-t-[40px] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-[var(--glass-bg)]/80 backdrop-blur-md pt-6 pb-2 px-6 z-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[var(--text-primary)] font-bold text-2xl tracking-tight">
              {t("home.library")}
            </h2>
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-[var(--glass-bg-hover)] active:bg-[var(--glass-bg-active)] transition-colors"
            >
              <CloseIcon size={22} className="text-[var(--text-secondary)]" />
            </button>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setTab("reading")}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                tab === "reading"
                  ? "bg-[var(--accent)]/15 text-[var(--accent)]"
                  : "text-[var(--text-secondary)] bg-black/5 dark:bg-white/5"
              }`}
            >
              {t("home.readingTab")}
            </button>
            <button
              onClick={() => setTab("liked")}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors flex items-center gap-1.5 ${
                tab === "liked"
                  ? "bg-[var(--accent)]/15 text-[var(--accent)]"
                  : "text-[var(--text-secondary)] bg-black/5 dark:bg-white/5"
              }`}
            >
              <HeartFilledIcon size={14} />
              {t("home.likedTab")}
            </button>
          </div>
        </div>

        <div className="px-6 pb-[calc(env(safe-area-inset-bottom,24px)+24px)] pt-4">
          {tab === "reading" && (
            <>
              {inProgressBooks.length > 0 ? (
                <div className="flex flex-col gap-3">
                  {inProgressBooks.map(({ book, progress }) => (
                    <button
                      key={book!.bookId}
                      onClick={() =>
                        handleBookRead(book!.bookId)
                      }
                      className="flex items-center gap-4 p-3 rounded-[24px] bg-[var(--glass-bg-hover)] backdrop-blur-md active:scale-[0.98] transition-all text-left shadow-lg"
                    >
                      {book!.aiCoverUrl ? (
                        <img
                          src={book!.aiCoverUrl}
                          alt=""
                          className="w-16 h-20 rounded-[16px] object-cover flex-shrink-0 shadow-md"
                        />
                      ) : (
                        <div className="w-16 h-20 rounded-[16px] bg-[var(--glass-bg)] flex-shrink-0 shadow-md" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-[var(--text-primary)] text-lg font-bold truncate">
                          {book!.aiTitle}
                        </p>
                        <p className="text-[var(--text-secondary)] text-sm font-medium mt-1">
                          {t("home.chapterProgress", { current: progress.lastChapter, total: book!.chapterCount })}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-[var(--text-muted)] text-sm text-center py-8">
                  {t("home.emptyReadingState")}
                </p>
              )}
            </>
          )}

          {tab === "liked" && (
            <>
              {likedBooksList.length > 0 ? (
                <div className="grid grid-cols-3 gap-4">
                  {likedBooksList.map((book) => (
                    <button
                      key={book.bookId}
                      onClick={() => handleBookRead(book.bookId)}
                      className="active:scale-95 transition-transform"
                    >
                      {book.aiCoverUrl ? (
                        <img
                          src={book.aiCoverUrl}
                          alt=""
                          className="w-full aspect-[9/19.5] rounded-[20px] object-cover shadow-lg"
                        />
                      ) : (
                        <div className="w-full aspect-[9/19.5] rounded-[20px] bg-[var(--glass-bg)] shadow-lg" />
                      )}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-[var(--text-muted)] text-sm text-center py-8">
                  {t("home.emptyLikedState")}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};


const Home = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const homeBooks = useUserSlice((state) => state.homeBooks);
  const readingProgress = useUserSlice((state) => state.readingProgress);

  const sharedBookId = useMemo(
    () => new URLSearchParams(window.location.search).get("bookId"),
    []
  );

  useEffect(() => {
    setMetaThemeColor("#000000");
  }, []);

  useEffect(() => {
    if (sharedBookId && homeBooks.length > 0) {
      logFunnelEvent("share:book_opened", { bookId: sharedBookId });
    }
  }, [sharedBookId, homeBooks.length]);

  const shuffledBooks = useMemo(() => {
    const arr = [...homeBooks];
    // Fisher-Yates shuffle
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    // Put the shared book first if present
    if (sharedBookId) {
      const idx = arr.findIndex((b) => b.bookId === sharedBookId);
      if (idx > 0) {
        const [book] = arr.splice(idx, 1);
        arr.unshift(book);
      }
    }
    return arr;
  }, [homeBooks, sharedBookId]);

  // Guest auth + data fetch on mount (mirrors native home pattern)
  useEffect(() => {
    const init = async () => {
      if (useUISlice.getState().isNewSession) {
        await new Promise<void>((resolve) => {
          useAuth.signInAsGuest({
            callback: async () => {
              useUISlice.getState().setIsNewSession(false);

              // Set content language to match app language if supported
              const lang = i18n.language;
              if (CONTENT_LANGUAGES.some((l) => l.code === lang)) {
                useUserSlice.setState({ contentLanguage: lang as ContentLanguage });
              }

              await Promise.all([
                useUserSlice.getState().library().home(),
                useUserSlice.getState().user().remoteConfig({
                  queryParams: { platform: getPlatform(), version },
                }),
                useUserSlice.getState().subscription().status(),
              ]);
              resolve();
            },
          });
        });
      } else {
        await Promise.all([
          useUserSlice.getState().library().home(),
          useUserSlice.getState().user().remoteConfig({
            queryParams: { platform: getPlatform(), version },
          }),
          useUserSlice.getState().subscription().status(),
        ]);
      }
      logFunnelEvent("[6] home:loaded", { bookCount: useUserSlice.getState().homeBooks.length });
    };
    init();
  }, []);

  const isMobile = useUISlice((state) => state.device.isMobile);
  const feedRef = useRef<HTMLDivElement>(null);
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);

  const [ageWarningBookId, setAgeWarningBookId] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const hasSeenScrollTutorial = useUserSlice((state) => state.hasSeenScrollTutorial);
  const [showScrollTutorial, setShowScrollTutorial] = useState(false);

  useEffect(() => {
    if (!hasSeenScrollTutorial && homeBooks.length > 1) {
      setShowScrollTutorial(true);
    }
  }, [hasSeenScrollTutorial, homeBooks.length]);

  const dismissScrollTutorial = useCallback(() => {
    setShowScrollTutorial(false);
    useUserSlice.getState().preference().setHasSeenScrollTutorial();
  }, []);

  const navigateToReader = useCallback(
    (bookId: string) => {
      const progress = readingProgress[bookId];
      if (progress) {
        router.push(
          `/web/reader?bookId=${bookId}&chapter=${progress.lastChapter}`
        );
      } else {
        router.push(`/web/reader?bookId=${bookId}&chapter=1`);
      }
    },
    [readingProgress, router]
  );

  const handleRead = useCallback((bookId: string) => {
    logFunnelEvent("[7] home:book_tapped", { bookId });
    setAgeWarningBookId(bookId);
  }, []);

  const lastReadBook = useMemo(() => {
    let best: { book: Book; lastChapter: number } | null = null;
    let bestTime = 0;
    for (const book of homeBooks) {
      const p = readingProgress[book.bookId];
      if (p) {
        const t = new Date(p.lastReadAt).getTime();
        if (t > bestTime) {
          bestTime = t;
          best = { book, lastChapter: p.lastChapter };
        }
      }
    }
    return best;
  }, [homeBooks, readingProgress]);

  // Shared feed content
  const renderFeedContent = (isDesktopView: boolean) => (
    <div
      ref={feedRef}
      className={`flex-1 overflow-y-scroll snap-y snap-mandatory no-scrollbar ${isDesktopView ? 'w-full' : ''}`}
      style={{ WebkitOverflowScrolling: "touch", scrollSnapStop: "always" }}
    >
      {shuffledBooks.length > 0
        ? shuffledBooks.map((book) => (
            <FeedCard
              key={book.bookId}
              book={book}
              onRead={() => handleRead(book.bookId)}
              onReadBook={handleRead}
              onSettingsOpenChange={setSettingsOpen}
              hideActions={!isMobile && !isDesktopView}
              isDesktop={isDesktopView}
            />
          ))
        : homeBooks.length === 0 && (
            <div className="h-[100dvh] flex items-center justify-center">
              {isDesktopView ? (
                <div className="h-[100dvh] w-full flex justify-center items-center py-4">
                  <div className="relative h-full aspect-[9/16] max-w-[calc(100vw-140px)] flex-shrink-0 animate-pulse">
                    <div className="absolute inset-0 w-full h-full rounded-2xl bg-white/5" />
                    <div className="absolute left-[calc(100%+24px)] bottom-8 flex flex-col items-center gap-5">
                      {[...Array(6)].map((_, i) => (
                        <div key={i} className="w-14 h-14 rounded-full bg-white/10" />
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Skeleton variant="HomeWeb" count={1} />
              )}
            </div>
          )}
    </div>
  );

  // Shared age warning modal
  const ageWarningModal = ageWarningBookId && (
    <div
      className="fixed inset-0 z-50"
      onClick={() => setAgeWarningBookId(null)}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-md" />
      <div className="absolute inset-0 flex items-center justify-center px-8">
        <div
          className="w-full max-w-sm bg-[var(--glass-bg)] backdrop-blur-2xl rounded-[32px] p-8 shadow-2xl border border-white/10 text-center"
          onClick={(e) => e.stopPropagation()}
        >
          <h2 className="text-[var(--text-primary)] text-xl font-bold mb-3">
            {t("ageWarning.title")}
          </h2>
          <p className="text-[var(--text-secondary)] text-base leading-relaxed mb-8">
            {t("ageWarning.description")}
          </p>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => {
                const bookId = ageWarningBookId;
                setAgeWarningBookId(null);
                navigateToReader(bookId);
              }}
              className="w-full h-14 rounded-full bg-white/20 backdrop-blur-md text-[var(--text-primary)] font-bold text-base active:scale-[0.97] transition-transform border border-white/20"
            >
              {t("ageWarning.over18")}
            </button>
            <button
              onClick={() => setAgeWarningBookId(null)}
              className="w-full h-14 rounded-full bg-white/10 text-[var(--text-secondary)] font-bold text-base active:scale-[0.97] transition-transform"
            >
              {t("ageWarning.under18")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Desktop layout
  if (!isMobile) {
    return (
      <WebContainer
        disableBottomSafeArea
        className="h-[100dvh] bg-[var(--bg-primary)] text-[var(--text-primary)] flex justify-center overflow-hidden relative"
      >
        {/* Continue reading bar */}
        {lastReadBook && (
          <div className="absolute top-8 left-1/2 -translate-x-1/2 z-20 w-full max-w-[400px] px-4">
            <button
              onClick={() => handleRead(lastReadBook.book.bookId)}
              className="flex items-center gap-3 p-2 pr-5 rounded-full bg-white/10 backdrop-blur-2xl shadow-[0_8px_32px_rgba(0,0,0,0.2)] w-full hover:bg-white/20 transition-all text-left group"
            >
              {lastReadBook.book.aiCoverUrl ? (
                <img
                  src={lastReadBook.book.aiCoverUrl}
                  alt=""
                  className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-white/10 flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-bold truncate drop-shadow-md">
                  {lastReadBook.book.aiTitle}
                </p>
                <p className="text-white/70 text-xs font-medium mt-0.5">
                  {t("home.chapterProgress", { current: lastReadBook.lastChapter, total: lastReadBook.book.chapterCount })}
                </p>
              </div>
              <span className="flex-shrink-0 px-4 py-2 rounded-full bg-white/20 text-white text-xs font-bold backdrop-blur-md group-hover:bg-white/30 transition-colors">
                {t("home.continueButton")}
              </span>
            </button>
          </div>
        )}
        
        <div className="w-full h-[100dvh] flex flex-col relative">
          {renderFeedContent(true)}
        </div>

        {/* Desktop overlays */}
        {isLibraryOpen && (
          <LibrarySheet
            onClose={() => setIsLibraryOpen(false)}
            onRead={handleRead}
          />
        )}
        {isLangOpen && (
          <LanguagePicker onClose={() => setIsLangOpen(false)} />
        )}
        {settingsOpen && (
          <div className="fixed inset-0 z-50">
            <div
              className="absolute inset-0 bg-[var(--bg-primary)]/40 backdrop-blur-sm"
              onClick={() => setSettingsOpen(false)}
            />
            <div className="absolute top-0 right-0 h-full w-full max-w-md bg-[var(--bg-primary)] shadow-2xl animate-slide-in-right">
              <Settings onClose={() => setSettingsOpen(false)} />
            </div>
          </div>
        )}

        {showScrollTutorial && (
          <ScrollTutorial onDismiss={dismissScrollTutorial} />
        )}
        {ageWarningModal}
      </WebContainer>
    );
  }

  // Mobile layout (unchanged)
  return (
    <WebContainer
      disableBottomSafeArea
      className="h-[100dvh] bg-[var(--bg-primary)] text-[var(--text-primary)] flex flex-col overflow-hidden"
    >
      {/* Sticky continue reading bar */}
      {lastReadBook && !settingsOpen && (
        <div className="absolute top-[env(safe-area-inset-top,16px)] left-0 right-0 z-20 px-4 pt-4">
          <button
            onClick={() => handleRead(lastReadBook.book.bookId)}
            className="flex items-center gap-3 p-2 pr-4 rounded-2xl bg-[var(--glass-bg)] backdrop-blur-2xl shadow-lg w-full active:scale-[0.97] transition-transform text-left"
          >
            {lastReadBook.book.aiCoverUrl ? (
              <img
                src={lastReadBook.book.aiCoverUrl}
                alt=""
                className="w-10 h-14 rounded-xl object-cover flex-shrink-0"
              />
            ) : (
              <div className="w-10 h-14 rounded-xl bg-white/10 flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-[var(--text-primary)] text-sm font-bold truncate">
                {lastReadBook.book.aiTitle}
              </p>
              <p className="text-[var(--text-secondary)] text-xs font-medium mt-0.5">
                {t("home.chapterProgress", { current: lastReadBook.lastChapter, total: lastReadBook.book.chapterCount })}
              </p>
            </div>
            <span className="flex-shrink-0 px-4 py-2 rounded-full bg-white/20 text-[var(--text-primary)] text-xs font-bold">
              {t("home.continueButton")}
            </span>
          </button>
        </div>
      )}

      {renderFeedContent(false)}

      {showScrollTutorial && (
        <ScrollTutorial onDismiss={dismissScrollTutorial} />
      )}
      {ageWarningModal}
    </WebContainer>
  );
};

export default Home;
